// Lead status values matching the backend
export type LeadStatus =
  | 'new'
  | 'discovered'
  | 'warmup'
  | 'warmup_done'
  | 'connection_sent'
  | 'connected'
  | 'contacted'
  | 'dm_sent'
  | 'followup_1_sent'
  | 'followup_2_sent'
  | 'followup_3_sent'
  | 'replied'
  | 'qualified'
  | 'meeting_booked'
  | 'won'
  | 'lost'

// Kanban columns map to human-readable stages
export type KanbanColumn = {
  readonly id: LeadStatus
  readonly label: string
  readonly color: string
  readonly headerClass: string
}

export const KANBAN_COLUMNS: readonly KanbanColumn[] = [
  {
    id: 'new',
    label: 'Discovered',
    color: 'gray',
    headerClass: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  },
  {
    id: 'connection_sent',
    label: 'Request Sent',
    color: 'blue',
    headerClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  },
  {
    id: 'connected',
    label: 'Connected',
    color: 'cyan',
    headerClass: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
  },
  {
    id: 'contacted',
    label: 'Messaged',
    color: 'sky',
    headerClass: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300',
  },
  {
    id: 'followup_1_sent',
    label: 'Follow-up 1',
    color: 'violet',
    headerClass: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300',
  },
  {
    id: 'followup_2_sent',
    label: 'Follow-up 2',
    color: 'fuchsia',
    headerClass: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-300',
  },
  {
    id: 'followup_3_sent',
    label: 'Follow-up 3',
    color: 'pink',
    headerClass: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
  },
  {
    id: 'replied',
    label: 'Replied',
    color: 'indigo',
    headerClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
  },
  // No 'qualified' column: the stage still exists in the data model (and
  // qualified leads render inside the Replied column), but it earned no
  // board real estate — owner call 2026-07-12.
  {
    id: 'meeting_booked',
    label: 'Meeting Booked',
    color: 'orange',
    headerClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  },
  {
    id: 'won',
    label: 'Won',
    color: 'green',
    headerClass: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  },
  {
    id: 'lost',
    label: 'Lost',
    color: 'red',
    headerClass: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  },
] as const

export type Lead = {
  readonly id: string
  readonly linkedin_url: string
  readonly first_name: string
  readonly last_name: string
  readonly full_name?: string
  readonly headline?: string
  readonly company?: string
  readonly job_title?: string
  readonly location?: string
  readonly photo_url?: string
  readonly status: LeadStatus
  readonly icp_score?: number
  readonly intent_score?: number
  readonly composite_score?: number
  readonly strategy?: string
  readonly campaign?: string
  readonly campaign_id?: string
  readonly messages_sent?: number
  readonly last_message_text?: string
  readonly last_activity_at?: string
  readonly created_at: string
  readonly enriched_at?: string
  readonly industry?: string
  readonly notes?: string
}

export type LeadStats = {
  readonly total: number
  readonly this_week: number
  readonly reply_rate: number
  readonly win_rate: number
  readonly by_status: Record<LeadStatus, number>
}

export type LeadFilters = {
  readonly status?: LeadStatus | 'all'
  readonly search?: string
  readonly score_min?: number
  readonly score_max?: number
  readonly campaign?: string
}

export type ViewMode = 'kanban' | 'table'

export type LeadSortField =
  | 'full_name'
  | 'company'
  | 'job_title'
  | 'composite_score'
  | 'status'
  | 'last_activity_at'
  | 'created_at'
  | 'campaign'

export type SortDirection = 'asc' | 'desc'

export type TableSort = {
  readonly field: LeadSortField
  readonly direction: SortDirection
}
